import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from '../hooks/useTheme';
import colorConfigs from '../../configs/colorConfigs';
type CustomFormatDatePickerProps = {
    dateFormat?: string;
    value: string;
    onChange: (value: string) => void;
    minDate?: string;
    maxDate?: string;
    className?: string;
    required?: boolean;
    showValidMessage?: boolean;
    disabled?: boolean;
    isModified?: boolean;
    leftShift?: boolean;
    today?: Moment;
}

const CustomFormatDatePicker = ({
    dateFormat = "DD/MM/yyyy",
    value,
    onChange,
    minDate,
    maxDate,
    className = "",
    required = false,
    showValidMessage = true,
    disabled = false,
    isModified = false,
    leftShift = false,
    today = moment().startOf("day"),
}: CustomFormatDatePickerProps) => {
    const { themeStyles } = useTheme();
    const pickerRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const normalizeMomentFormat = (fmt: string) =>
        fmt.replace(/yyyy/gi, "YYYY").replace(/dd/gi, "DD");
    const displayFormat = normalizeMomentFormat(dateFormat);

    const parseToMoment = (s?: string | null) => {
        if (!s || s.trim() === "") return null;
        const tryFormats = [
            displayFormat,
            "MM/DD/YYYY",
            "DD/MM/YYYY",
            "YYYY-MM-DD",
            "YYYY/MM/DD",
            "DD.MM.YYYY",
            "DD-MMM-YYYY",
            moment.ISO_8601,
        ];
        for (const fmt of tryFormats) {
            const m = moment(s, fmt, true);
            if (m.isValid()) return m;
        }
        const fallback = moment(s);
        return fallback.isValid() ? fallback : null;
    };

    const toIso = (m: Moment | null) => (m ? m.format("YYYY-MM-DD") : "");

    const [formattedValue, setFormattedValue] = useState<string>("");
    const [selectedDate, setSelectedDate] = useState<Moment | null>(null);
    const [currentMonth, setCurrentMonth] = useState<Moment>(moment());
    const [showCalendar, setShowCalendar] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const min = parseToMoment(minDate ?? null);
    const max = parseToMoment(maxDate ?? null);

    useEffect(() => {
        const m = parseToMoment(value);

        if (dateFormat === "DD-MMM-YYYY" && isFocused) {
            // While typing, show numeric month
            setFormattedValue(m?.format("DD-MM-YYYY") || "");
            setSelectedDate(m);
            setCurrentMonth((m ?? moment()).clone());
        } else {
            setFormattedValue(m?.format(displayFormat) || "");
            setSelectedDate(m);
            setCurrentMonth((m ?? moment()).clone());
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, dateFormat]);



    useEffect(() => {
        if (!selectedDate && isModified && !isFocused) {
            setFormattedValue(moment()?.format(displayFormat) || "");
            setSelectedDate(moment());
            setCurrentMonth(moment().clone());
            onChange(toIso(moment()));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, isModified, isFocused])


    const getFormatMeta = (fmt: string) => {
        const sep = fmt.match(/[^A-Za-z]/)?.[0] ?? "/";
        const parts = fmt.split(sep);
        const groupLengths = parts.map((p) => p.length);
        const maxDigits = groupLengths.reduce((a, b) => a + b, 0);
        const separatorsCount = Math.max(0, parts.length - 1);
        return { sep, parts, groupLengths, maxDigits, separatorsCount };
    };
    const fmtMeta = getFormatMeta(
        dateFormat === "DD-MMM-YYYY" && isFocused ? "DD-MM-YYYY" : displayFormat
    );

    const insertSeparators = (digits: string) => {
        const { sep, groupLengths } = fmtMeta;
        let result = "";
        let cursor = 0;
        for (let i = 0; i < groupLengths.length; i++) {
            const take = groupLengths[i];
            const chunk = digits.slice(cursor, cursor + take);
            result += chunk;
            cursor += chunk.length;
            if (i < groupLengths.length - 1 && digits.length > cursor)
                result += sep;
            if (cursor >= digits.length) break;
        }
        return result;
    };

    const isInRange = (day: Moment) => {
        if (min && day.isBefore(min, "day")) return false;
        if (max && day.isAfter(max, "day")) return false;
        return true;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        setFormattedValue(raw);
        let digitsOnly = raw.replace(/\D/g, "");

        const truncated = digitsOnly.slice(0, fmtMeta.maxDigits);

        // Insert separators while typing
        const formatted = insertSeparators(truncated);


        let parsed: moment.Moment | null = null;

        if (dateFormat === "DD-MMM-YYYY") {
            // Parse as DD-MM-YYYY first
            parsed = moment(formatted, "DD-MM-YYYY", true);

            if (parsed.isValid() && isInRange(parsed)) {
                setSelectedDate(parsed);
                setCurrentMonth(parsed.clone());
                onChange(toIso(parsed)); // Emit ISO only when valid
            } else if (raw.trim() === "" && !isModified) {
                setSelectedDate(null); // Only clear when field is fully empty
                onChange("")
            }
            else {
                setSelectedDate(null);
            }
        } else {
            // Normal parse for other formats
            parsed = moment(formatted, dateFormat, true);
            if (parsed.isValid() && isInRange(parsed)) {
                setSelectedDate(parsed);
                setCurrentMonth(parsed.clone());
                onChange(toIso(parsed)); // Emit ISO only when valid
            } else if (raw.trim() === "" && !isModified) {
                setSelectedDate(null); // Only clear when field is fully empty
                onChange("")
            }
            else {
                setSelectedDate(null);
            }

        }
    }

    const handleDayClick = (day: Moment) => {
        if (!isInRange(day)) return;
        setSelectedDate(day.clone());
        setCurrentMonth(day.clone());
        setShowCalendar(false);
        const iso = toIso(day);
        setFormattedValue(day.format(displayFormat));
        onChange(iso);
    };

    const clearDate = () => {
        if (!isModified) {
            setFormattedValue("");
            setSelectedDate(null);
            setCurrentMonth(moment());
            setShowCalendar(false);
            onChange("");
        }
        else {
            setFormattedValue(moment().format(displayFormat) || "");
            setSelectedDate(moment());
            setCurrentMonth(moment());
            setShowCalendar(false);
            onChange(toIso(moment()));
        }
    };

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
        if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
            setShowCalendar(false);
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        if (dateFormat === "DD-MMM-YYYY") {
            if (selectedDate) {
                setFormattedValue(selectedDate.format("DD-MM-YYYY")); // show numeric month
            } else if (formattedValue) {
                // convert current text to numeric month if possible
                const parsed = moment(formattedValue, "DD-MMM-YYYY", true);
                if (parsed.isValid()) {
                    setFormattedValue(parsed.format("DD-MM-YYYY"));
                }
            }
        }
        setShowCalendar(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
        if (dateFormat === "DD-MMM-YYYY" && formattedValue) {
            const parsed = moment(formattedValue, ["DD-MM-YYYY", "DD-MMM-YYYY"], true);
            if (parsed.isValid()) {
                setFormattedValue(parsed.format("DD-MMM-YYYY")); // switch back to MMM
            }
        }
    }

    useEffect(() => {
        if (showCalendar) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [showCalendar]);

    const renderHeader = () => {
        const prevMonth = currentMonth.clone().subtract(1, "month");
        const nextMonth = currentMonth.clone().add(1, "month");
        const canGoPrev =
            !min || prevMonth.endOf("month").isSameOrAfter(min, "day");
        const canGoNext =
            !max || nextMonth.startOf("month").isSameOrBefore(max, "day");

        return (
            <div className="flex justify-between items-center mb-2">
                <button
                    type="button"
                    onClick={() => canGoPrev && setCurrentMonth(prevMonth)}
                    className={`px-2 py-1 rounded ${canGoPrev
                        ? `${themeStyles.tabs.textActiveTextColor} ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`
                        : `${themeStyles.tabs.textInActiveTextColor} cursor-not-allowed`
                        }`}
                    disabled={!canGoPrev}
                >
                    ◀
                </button>
                <span className={`${themeStyles.main.textHeadingColor} font-semibold`}>
                    {currentMonth.format("MMMM YYYY")}
                </span>
                <button
                    type="button"
                    onClick={() => canGoNext && setCurrentMonth(nextMonth)}
                    className={`px-2 py-1 rounded ${canGoNext
                        ? `${themeStyles.tabs.textActiveTextColor} ${themeStyles.sideBar.tailwindMenuItemsHoverColor}`
                        : `${themeStyles.tabs.textInActiveTextColor} cursor-not-allowed`
                        }`}
                    disabled={!canGoNext}
                >
                    ▶
                </button>
            </div>
        );
    };

    const renderDays = () => {
        const weekDays = moment.weekdaysShort().map((d) => d.slice(0, 2));
        return (
            <div className="grid grid-cols-7 mb-1">
                {weekDays.map((day) => (
                    <div
                        key={day}
                        className={`${themeStyles.main.textHeadingColor} text-xs font-medium text-center`}
                    >
                        {day}
                    </div>
                ))}
            </div>
        );
    };

    const renderCells = () => {
        const monthStart = currentMonth.clone().startOf("month");

        const startDate = monthStart.clone().startOf("week");
        const day = startDate.clone();
        const rows: JSX.Element[] = [];

        for (let row = 0; row < 6; row++) {
            const cols: JSX.Element[] = [];
            for (let col = 0; col < 7; col++) {
                const cloneDay = day.clone();
                const isCurrentMonth = cloneDay.month() === currentMonth.month();

                const isSelected = selectedDate && cloneDay.isSame(selectedDate, "day");
                const disabled = !isInRange(cloneDay);

                const normalizedToday = (today ?? moment()).clone().startOf("day");
                const todayStr = normalizedToday?.format("YYYY-MM-DD");
                const isToday = cloneDay?.format("YYYY-MM-DD") === todayStr;              

                cols.push(
                    <div
                        key={cloneDay.toString()}
                        onClick={() => !disabled && handleDayClick(cloneDay)}                     
                        className={`w-full flex items-center justify-center text-sm p-1 rounded
                            ${disabled
                                ? `${themeStyles.tabs.textInActiveTextColor} cursor-not-allowed pointer-events-none opacity-30`
                                : `cursor-pointer ${!isSelected && !isToday ? themeStyles.sideBar.tailwindMenuItemsHoverColor : ""}`
                            }
                            ${isCurrentMonth
                                ? themeStyles.tabs.textActiveTextColor
                                : themeStyles.tabs.textInActiveTextColor}
                            ${isSelected
                                ? `${colorConfigs.btn.main.tailwind} text-white`
                                : ""}
                            ${isToday
                                ? "border border-[#167AC3] text-[#167AC3] font-semibold"
                                : ""}
                            `}
                    >
                        {cloneDay.date()}
                    </div>
                );

                day.add(1, "day");
            }

            rows.push(
                <div key={row} className="grid grid-cols-7 mb-1">
                    {cols}
                </div>
            );
        }

        return <div>{rows}</div>;
    };

    return (
        <div className={`${isModified || leftShift ? 'w-max' : 'w-full'}`} ref={pickerRef}>
            <input
                ref={inputRef}
                type="text"
                placeholder={
                    dateFormat === "DD-MMM-YYYY" ? "DD-MM-YYYY" : dateFormat
                }
                value={formattedValue}
                onChange={handleInputChange}
                maxLength={fmtMeta.maxDigits + fmtMeta.separatorsCount}
                onFocus={handleFocus}
                onBlur={handleBlur}
                className={className ? className : `w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none !text-left text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                required={required}
                disabled={disabled}
            />

            {showValidMessage &&
                formattedValue &&
                !moment(formattedValue, (dateFormat === "DD-MMM-YYYY" && isFocused ? "DD-MM-YYYY" : displayFormat), true).isValid() && (
                    <p className="text-xs text-red-500 mt-1">
                        Use format {dateFormat === "DD-MMM-YYYY" ? "DD-MM-YYYY" : dateFormat}
                    </p>
                )}

            {showCalendar && (
                <div className={`absolute mt-2 ${themeStyles.main.tailwindMainBg} border rounded shadow p-4 z-[999] w-64 ${isModified || leftShift ? 'right-4' : ''}`}>
                    {renderHeader()}
                    {renderDays()}
                    {renderCells()}
                    <button
                        type="button"
                        onClick={clearDate}
                        className="mt-2 px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                    >
                        Clear
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomFormatDatePicker